import { useEffect, useState } from "react";

/**
 * "Hello" in the languages of the people the portal serves and of the world RWS works in. They are content in their own languages, not
 * interface text, so they are not in the translation catalogue: each carries its language tag instead.
 */
const greetings: readonly { text: string; lang: string }[] = [
  { text: "Hello", lang: "en" },
  { text: "Bună", lang: "ro" },
  { text: "Hallo", lang: "de" },
  { text: "Bonjour", lang: "fr" },
  { text: "こんにちは", lang: "ja" },
  { text: "Hola", lang: "es" },
  { text: "你好", lang: "zh" },
  { text: "Ciao", lang: "it" },
  { text: "नमस्ते", lang: "hi" },
  { text: "Olá", lang: "pt" },
  { text: "Cześć", lang: "pl" },
  { text: "안녕하세요", lang: "ko" },
  { text: "Hej", lang: "sv" },
  { text: "Merhaba", lang: "tr" },
];

const glyphs = "▚▞▖▗▘▝01アカサタナ<>/{}#*+=";
const holdMs = 2600;
const decodeMs = 700;

/**
 * Cycles through `greetings`, each one decoding out of a scramble of glyphs, as a model's output resolves into words. Decorative (the
 * page's heading carries the meaning), so it is hidden from assistive technology. Shows the first greeting, still, when `animate` is
 * false.
 *
 * @param props.animate - Whether the greeting cycles.
 * @param props.className - Classes for the greeting element.
 */
export function GreetingScramble({ animate, className }: { animate: boolean; className?: string }): React.JSX.Element {
  const [index, setIndex] = useState(0);
  const [shown, setShown] = useState(greetings[0].text);

  useEffect(() => {
    if (!animate) {
      setShown(greetings[index].text);
      return;
    }

    let frame = 0;
    const timer = window.setTimeout(() => {
      const next = (index + 1) % greetings.length;
      const target = Array.from(greetings[next].text);
      const started = performance.now();

      const tick = (now: number): void => {
        const settled = Math.floor((Math.min((now - started) / decodeMs, 1)) * target.length);
        setShown(
          target.map((character, position) => (position < settled ? character : glyphs[Math.floor(Math.random() * glyphs.length)])).join(""),
        );
        if (settled < target.length) {
          frame = requestAnimationFrame(tick);
        } else {
          setIndex(next);
        }
      };

      frame = requestAnimationFrame(tick);
    }, holdMs);

    return () => {
      window.clearTimeout(timer);
      cancelAnimationFrame(frame);
    };
  }, [animate, index]);

  return (
    <div className={className} aria-hidden="true" lang={greetings[index].lang}>
      {shown}
    </div>
  );
}
